import {
  Users, BookOpen, UserCog, Briefcase, Globe,
  Calendar, Brain, Link2, Palette, Code2, Building2,
} from "lucide-react";

/**
 * Plan Module Categories
 * ─────────────────────────────────────────────────────────────
 * Mirrors the admin permission‑category structure so that Super
 * Admins define plan access at the **module** level (on/off),
 * NOT at the CRUD level.
 *
 * Each category maps to one or more boolean feature toggles in
 * the Plan model.
 *
 * `core: true` means the module is always available and its
 * capacity is controlled by numeric limits (maxStudentsPerBatch, etc.).
 */
export const planModuleCategories = [
  /* ─── Core modules (always ON — limited by numeric caps) ──────────── */
  {
    category: "Student Management",
    icon: Users,
    description: "Student records, enrollments, and profiles",
    core: true,
    limitKeys: ["maxStudentsPerBatch"],
    modules: [],
  },
  {
    category: "Course & Batch Management",
    icon: BookOpen,
    description: "Courses, batches, syllabus, and scheduling",
    core: true,
    limitKeys: ["maxCourses", "maxBatches"],
    modules: [],
  },
  {
    category: "Instructor Management",
    icon: UserCog,
    description: "Instructor profiles and schedule management",
    core: true,
    limitKeys: ["maxInstructors"],
    modules: [],
  },

  /* ─── Toggleable modules ──────────────────────────────────────────── */
  {
    category: "Fee Management",
    icon: Briefcase,
    description: "Fee collection, payments, invoices, and discounts",
    modules: [
      { key: "feeManagement", label: "Fee Management", description: "Full fee collection, payment tracking, invoices, and discount management" },
    ],
  },
  {
    category: "Enquiry Management",
    icon: Globe,
    description: "Lead tracking, follow‑ups, and conversion",
    modules: [
      { key: "enquiryManagement", label: "Enquiry Management", description: "Lead capture, follow‑up tracking, and convert‑to‑student workflow" },
    ],
  },
  {
    category: "Assessments & Practice",
    icon: Calendar,
    description: "Assignments, interviews, and coding practice",
    modules: [
      { key: "assignments",      label: "Assignments",        description: "Create and manage assignments, grading, and student submissions" },
      { key: "quizAssessment",  label: "Quiz & Assessments",  description: "Quiz bank, timed assessments, analytics, and bulk operations" },
      { key: "interviews",      label: "Interviews",          description: "Schedule and manage interview sessions with candidates" },
      { key: "codingPractice",  label: "Coding Practice",     description: "In‑browser coding environment for student practice" },
      { key: "selfPacedCourses", label: "Self‑Paced Learning", description: "Enable self‑paced courses for students to learn at their own pace" },
    ],
  },
  {
    category: "AI Features",
    icon: Brain,
    description: "AI‑powered learning and assessment tools",
    modules: [
      { key: "aiMock",         label: "AI Mock Interview", description: "AI‑driven mock interviews with real‑time feedback" },
      { key: "aiLearning",     label: "AI Learning",       description: "Personalised AI tutor for student self‑study" },
      { key: "aiQuestionBank", label: "AI Question Bank",  description: "AI‑generated question banks and quiz creation" },
      { key: "aiDoubtSolving", label: "AIvo – AI Doubt Assistant", description: "AI‑powered doubt solving chatbot for students" },
      { key: "aiWhatsappAgent", label: "AI WhatsApp Agent", description: "AI‑powered WhatsApp replies for leads and students (Suggest / Auto modes)" },
      { key: "aiWhatsappLeadFollowup", label: "AI Lead Follow‑up", description: "Automated WhatsApp drip follow‑ups for stale leads" },
    ],
  },
  {
    category: "Integrations",
    icon: Link2,
    description: "Third‑party service connections",
    modules: [
      { key: "zoomV2",     label: "Zoom Integration",        description: "Auto‑create Zoom meetings and sync recordings" },
      { key: "platformHostedZoom", label: "Platform Hosted Zoom", description: "Managed virtual classrooms from a shared host pool — no Zoom account required" },
      { key: "recordings", label: "Class Recordings",         description: "Store and share recorded class sessions" },
      { key: "whatsapp",   label: "WhatsApp Notifications",   description: "Send automated notifications via WhatsApp" },
      { key: "zohoCrm",    label: "Zoho CRM Integration",     description: "Sync enquiries as leads with Zoho CRM" },
    ],
  },
  {
    category: "Branding & Customization",
    icon: Palette,
    description: "White‑labeling and custom branding options",
    modules: [
      { key: "customBranding", label: "Custom Branding", description: "Custom logo, colours, and brand name across the platform" },
      { key: "customDomain",   label: "Custom Domain",   description: "Use your own domain (e.g. app.yourinstitute.com)" },
    ],
  },
  {
    category: "Advanced",
    icon: Code2,
    description: "Developer and advanced features",
    modules: [
      { key: "apiAccess", label: "API Access", description: "REST API access for external integrations and automation" },
      { key: "enquiryFormBuilder", label: "Enquiry Form Builder", description: "Custom enquiry forms with embed script for external websites" },
      { key: "secureVideoPlayer", label: "Secure Video Player", description: "Require desktop app for video playback with OS-level screen capture protection" },
    ],
  },
  {
    category: "Branch Management",
    icon: Building2,
    description: "Multi-branch operations and management",
    modules: [
      { key: "multiBranch", label: "Multi-Branch", description: "Manage multiple branches with separate batches, enquiries, and staff assignments" },
    ],
  },
];

/** All toggleable feature keys (excludes core modules) */
export const ALL_PLAN_FEATURE_KEYS = planModuleCategories
  .flatMap((c) => c.modules.map((m) => m.key));
